// Central theme config. Change colors here — components reference these constants.
// Raw hex values: used in non-Tailwind props (ReactFlow Background, edge options).
// Class strings: Tailwind scans this file, so complete class names are safe here.

export const THEME = {
  // ── Raw hex (non-Tailwind props) ─────────────────────────────────────────
  // ReactFlow CSS vars are applied inline in Viewport.tsx from these values.
  // To retheme: change here — index.css no longer owns these colors.
  raw: {
    canvasBg:            "#09090b",              // zinc-950
    dotBackground:       "#27272a",              // zinc-800
    edgeStroke:          "#4f46e5",              // indigo-600 (idle line, per mock)
    edgeStrokeSelected:  "#818cf8",              // indigo-400
    edgeArrowColor:      "#6366f1",              // indigo-500 (open-chevron arrowhead, per mock)
    edgeDotColor:        "#a5b4fc",              // indigo-300 (flow dot, per mock)
    edgeStrokeWidth:     1.8,                    // idle line weight (mock)
    edgeStrokeWidthSelected: 2.4,               // active/selected line weight (mock)
    edgeOpacity:         0.5,                    // idle-lit edge opacity (mock)
    edgeOpacitySelected: 0.95,                   // active edge opacity (mock)
    edgeHitboxWidth:     20,
    edgeConnection:      "#818cf8",
    handleBg:            "rgba(99,102,241,0.5)",
    handleBorder:        "rgba(129,140,248,0.7)",
    controlsBg:          "#18181b",              // zinc-900
    controlsBgHover:     "#27272a",              // zinc-800
    controlsText:        "#a1a1aa",              // zinc-400
    controlsTextHover:   "#fafafa",              // zinc-50
    controlsBorder:      "#3f3f46",              // zinc-700

    // Enlightenment glow (inline box-shadow on NodeCard). A node is the light
    // source: when enlightened it casts an indigo halo onto the dark canvas (per
    // the design mock's `tt-glowpulse`). Both values carry the same black elevation
    // shadow so the drop stays put; only the indigo bloom (0 0 …) appears. Indigo
    // = #6366f1 / rgba(99,102,241).
    nodeGlowUnlit:       "0 4px 14px rgba(0,0,0,0.35)",
    nodeGlowLit:         "0 4px 14px rgba(0,0,0,0.35), 0 0 22px 2px rgba(99,102,241,0.30), 0 0 48px 14px rgba(99,102,241,0.12)",
    // Brighter indigo halo the selected node pulses toward (loops lit ↔ active).
    nodeGlowActive:      "0 4px 14px rgba(0,0,0,0.45), 0 0 30px 4px rgba(99,102,241,0.50), 0 0 66px 20px rgba(99,102,241,0.22)",

    // Atmospheric canvas layers (painted behind ReactFlow in Viewport.tsx as
    // absolutely-positioned, pointer-events:none divs). Values from the design
    // mock: a warm radial wash, a very faint white dot grid, and a soft indigo
    // bloom at the optical centre. Keep the base colour ending on `canvasBg`.
    canvasGradient:      "radial-gradient(1200px 700px at 62% 30%, #121218 0%, #0b0b0e 55%, #09090b 100%)",
    canvasBloom:         "radial-gradient(600px 380px at 50% 44%, rgba(99,102,241,0.10), transparent 70%)",
    dotGridImage:        "radial-gradient(#ffffff 1px, transparent 1px)",
    dotGridSize:         "26px 26px",
    dotGridOpacity:      0.028,

    // Reveal HUD step dots (mock): amber when filled (≤ current step) with a soft
    // glow, muted zinc when not yet reached.
    revealDotLit:        "#f59e0b",              // amber-500
    revealDotDim:        "#3f3f46",              // zinc-700
    revealDotGlow:       "0 0 6px rgba(245,158,11,0.9)",
  },

  // ── Canvas viewport (Cinematic Dark) ───────────────────────────────────────
  canvas: {
    bg:        "bg-zinc-950",
    loading:   "bg-zinc-950/70",
    colorMode: "dark" as const,
  },

  // ── Sidebar / panels (dark) ──────────────────────────────────────────────
  panel: {
    bg:           "bg-canvas-node",
    border:       "border-canvas-node-border",
    borderRight:  "border-r border-canvas-node-border",
    borderBottom: "border-b border-canvas-node-border",
    borderTop:    "border-t border-canvas-node-border",
    borderLeft:   "border-l border-canvas-node-border",
    text:         "text-canvas-text",
    muted:        "text-canvas-muted",
    hover:        "hover:bg-zinc-800",
    hoverText:    "hover:text-zinc-50",
    activeItem:   "bg-indigo-950/50 text-indigo-200 border border-indigo-900",
    input:        "bg-zinc-900 border border-zinc-700 text-zinc-100 outline-none",
  },

  // ── Left sidebar (cool slate — deliberately distinct from the graphite canvas)
  // Single source of truth for the sidebar look. Arbitrary hex values are
  // complete class names, so Tailwind scans them here safely. Change the surface
  // / accent once and every sidebar component follows.
  sidebar: {
    surface:      "bg-[#161b22]",                 // expanded panel surface (cool slate)
    rail:         "bg-[#12161c]",                 // collapsed slim strip (a touch darker)
    divider:      "border-white/[0.06]",          // hairline dividers (use with border-*)
    borderBottom: "border-b border-white/[0.06]",
    borderTop:    "border-t border-white/[0.06]",
    heading:      "font-mono text-slate-500",      // uppercase section labels / meta (mock: mono eyebrows)
    text:         "text-slate-200",               // primary label
    muted:        "text-slate-400",               // secondary
    rowText:      "text-slate-400",               // idle row label
    rowHover:     "hover:bg-white/[0.05] hover:text-slate-100",
    active:       "bg-indigo-500/15 text-slate-50",   // selected row
    activeBar:    "bg-indigo-400",                // active leaf accent bar
    accent:       "text-indigo-400",              // icons / active accent
    accentSoft:   "text-indigo-400/80",
    iconIdle:     "text-slate-500",
    input:        "bg-[#0f141a] border border-white/10 text-slate-100 placeholder:text-slate-500 outline-none focus:border-indigo-500/60",
    dangerHover:  "hover:text-rose-400",
    // ── Mock hover/pin rail (design-mock `railStyle` family) ──
    railSurface:  "bg-[#0f0f13]/95 backdrop-blur-md",        // rail material (mock rgba(15,15,19,.96))
    railSeam:     "shadow-[1px_0_0_rgba(255,255,255,0.07)]", // right hairline seam
    railExpandShadow: "shadow-[20px_0_40px_-20px_rgba(0,0,0,0.6)]", // cast when expanded
    // font-extrabold (800) is JetBrains Mono's real ceiling — no 900/black cut exists
    // for this font. A thin same-color text-stroke fakes extra thickness past that.
    mapsLabel:    "font-mono text-[11px] font-extrabold tracking-[0.12em] text-zinc-400 uppercase [-webkit-text-stroke:0.4px_currentColor]", // "WORKSPACES" header — matches the workspace-name section title (WorkspaceView) and the toolbar's idle icon color (toolbar.buttonInactive)
    pinIdle:      "text-zinc-500 hover:text-zinc-300 hover:bg-white/5",  // mock #71717a
    pinActive:    "text-indigo-300 bg-indigo-500/20",         // mock #a5b4fc on rgba(99,102,241,.2)
    // Workspace circle badge (mock `circleStyle`) — neutral fill, NOT a per-project
    // colour; the active one gets a faint accent tint + border + glow.
    circleIdle:   "bg-[#18181b] border border-[#2a2a31] text-slate-400",
    circleActive: "bg-indigo-500/[0.15] border border-indigo-400 text-slate-50",
    // Canvas top bar (mock TOP BAR) — floats over the canvas (transparent, like
    // the mock); only the wordmark + breadcrumb are visible.
    topBarCrumb:  "text-zinc-400",                  // "workspace / map" breadcrumb (mock #a1a1aa)
    topBarCrumbStrong: "text-zinc-200",             // active crumb (mock #e4e4e7)
    topBarDivider: "bg-[#27272a]",
  },

  // ── Toolbar (dark, matches the graphite canvas) ──────────────────────────
  toolbar: {
    container:      "bg-zinc-900 border border-white/10",
    divider:        "bg-zinc-700",
    // Desktop floating bar: the drag grip at the bar's left edge (dim, brightens on hover).
    dragHandle:     "text-zinc-600 hover:text-zinc-300",
    buttonActive:   "bg-indigo-500/20 text-indigo-400",
    buttonInactive: "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-700/50",
    // Mobile scroll-hint fades: opaque container color at the edge → transparent
    // inward. Left edge fades right; right edge fades left. Matches `container`.
    fadeLeft:       "bg-gradient-to-r from-zinc-900 to-transparent",
    fadeRight:      "bg-gradient-to-l from-zinc-900 to-transparent",
  },

  // ── Dark popups (toolbar dropdowns, context menus, row menus) ────────────
  popup: {
    container:    "bg-zinc-900 border border-white/10",
    text:         "text-zinc-100",
    muted:        "text-zinc-500",
    input:        "bg-zinc-800 border border-zinc-700",
    inputText:    "text-zinc-100 placeholder:text-zinc-500",
    itemHover:    "hover:bg-zinc-700/50",
    sectionLabel: "font-mono text-zinc-500",
  },

  // ── Node cards on canvas ──────────────────────────────────────────────────
  // Surface/border route through the canvas tokens (#141419 / #2a2a31, per the
  // mock). No shadow-* here — NodeCard's glow is an inline box-shadow that would
  // override a Tailwind shadow anyway (see theme.md §Enlightenment).
  node: {
    default:       "bg-canvas-node border border-canvas-node-border",
    label:         "text-canvas-text",
    dashedBorder:  "bg-canvas-node border border-dashed border-canvas-node-border",
    terminal:      "bg-canvas-bg border border-canvas-node-border",
    terminalLabel: "text-emerald-400 font-mono",
    note:          "bg-amber-950/30 border border-amber-900/50",
    noteLabel:     "text-amber-200",
    badge:         "bg-zinc-800",
    // Type kicker above the node label: mono, uppercase, tracked, extrabold —
    // small type on the dark canvas needs maximum weight to stay legible.
    // Color comes from the node's accent (NodeCard), not from here.
    typeLabel:     "font-mono font-extrabold uppercase tracking-[0.13em]",
  },

  // ── Shapes on canvas (SVG fill/stroke are raw hex) ───────────────────────
  shape: {
    fill:          "#27272a",   // zinc-800
    stroke:        "#71717a",   // zinc-500
    circleBg:      "bg-zinc-800 border border-zinc-500 shadow-sm",
    containerBg:   "bg-zinc-900/50 border border-dashed border-zinc-600",
    textboxBg:     "bg-zinc-950 border border-zinc-600 shadow-sm",
    textboxText:   "text-zinc-200",
  },

  // ── Node-page cards (expandable Pages panel, per design mock) ─────────────
  // Section accents for the expandable page card body. Distinct hues per
  // section type (mock): summary=indigo, "you asked"=violet, snippet=zinc,
  // sub-subjects=cyan. Single source of truth — never inline these in the drawer.
  pageCard: {
    // Panel shell (the floating card that holds the page cards). shell = solid
    // frosted surface (drawer/panel treatments); shellGlass = more translucent
    // (glass treatment). Both blur the canvas behind them.
    shell:        "bg-[#111218]/95 border border-white/[0.09] backdrop-blur-xl shadow-[0_30px_80px_-24px_rgba(0,0,0,0.85),0_0_60px_-30px_rgba(99,102,241,0.5)]",
    shellGlass:   "bg-[#18181f]/60 border border-white/[0.09] backdrop-blur-xl shadow-[0_30px_80px_-24px_rgba(0,0,0,0.85),0_0_60px_-30px_rgba(99,102,241,0.5)]",
    surface:      "bg-white/[0.02] border border-white/[0.06]",
    surfaceHover: "hover:bg-white/[0.04]",
    label:        "font-mono uppercase tracking-[0.14em]",  // shared section-label typography
    // Header kicker + node-name title (NodePageDrawer). Same mono/uppercase/
    // tracked/zinc-400 family as sidebar.mapsLabel, but each step down a weight
    // (kicker thinner than mapsLabel's extrabold, title thinner still) so the
    // drawer header doesn't out-bold the sidebar it opens from.
    drawerKicker: "font-mono text-[10px] font-bold tracking-[0.12em] text-zinc-400 uppercase [-webkit-text-stroke:0.2px_currentColor]",
    drawerTitle:  "font-mono text-[17px] font-semibold tracking-[0.12em] text-zinc-400 uppercase",
    note:         "text-slate-400",   // "Notes" — the page body/content preview
    summary:      "text-indigo-500",           // mock #6366f1 (summary eyebrow)
    summaryText:  "text-[#c4c4cc]",             // mock summary body colour
    ask:          "text-violet-400",
    askItem:      "text-canvas-text bg-violet-400/[0.08] border-l-2 border-violet-400/50",
    snippet:      "text-canvas-muted",
    snippetBlock: "text-[#a5f3c9] bg-[#0a0a0d] border border-[#1f1f23]",  // mock snippet <pre>
    child:        "text-cyan-400",
    childRail:    "border-cyan-400/25",
  },

  // MCP access page (/settings/mcp). One scale in one place so the steps stay
  // proportional — bumping a size should not mean hunting literals across the
  // page and its three components. Compose the eyebrow tokens with
  // `pageCard.label` above for the shared mono/uppercase/tracking treatment.
  //
  // Greys are zinc-300/400 rather than canvas-muted (#71717a): on the near-black
  // canvas bg that sits around 4:1 contrast, which is hard to read for body copy
  // and column headers. zinc-400 lands near 7:1, zinc-300 near 12:1.
  adminPage: {
    tabActive:    "bg-red-500/10 text-red-500",
    dangerButton: "bg-red-600 hover:bg-red-500 text-white",
    safeButton:   "bg-emerald-600 hover:bg-emerald-500 text-white",
    blockChip:    "bg-red-600/10 text-red-500",
    unblockChip:  "bg-emerald-600/10 text-emerald-500",
    errorText:    "text-red-500",
    rowBorder:    "border-white/10",
  },
  mcpPage: {
    title:        "text-[28px] font-semibold tracking-tight text-canvas-text",
    intro:        "text-[15px] leading-relaxed text-zinc-400",
    sectionLabel: "text-[13px] font-bold text-zinc-300",     // YOUR TOKENS / SETUP
    fieldLabel:   "text-[12px] font-bold text-zinc-300",     // CLIENT
    columnLabel:  "text-[12px] font-semibold text-zinc-400", // table headers
    hint:         "text-[13px] text-zinc-400",               // "Run in your terminal"
    cell:         "text-[13px] text-zinc-300",
    cellMuted:    "text-[13px] text-zinc-400",
    control:      "text-[14px]",                             // inputs, selects, buttons
    code:         "text-[13px] font-mono",                   // snippet + raw token blocks
    dialogTitle:  "text-[16px] font-semibold",
  },

  // Dark scrollbar for overflow areas on dark surfaces. Without this the UA
  // paints a light track, which reads as a white bar across a near-black panel.
  // Both vendor syntaxes are needed: `scrollbar-*` for Firefox, the webkit
  // pseudo-elements for Chrome and Safari.
  scrollbar: {
    dark:
      "[scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.18)_transparent] " +
      "[&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar]:w-1.5 " +
      "[&::-webkit-scrollbar-track]:bg-transparent " +
      "[&::-webkit-scrollbar-thumb]:bg-white/15 [&::-webkit-scrollbar-thumb]:rounded-full " +
      "[&::-webkit-scrollbar-thumb:hover]:bg-white/25",
  },

  // Buttons. Previously composed ad hoc at each call site (ConfirmDialog hard-
  // coded bg-red-600), which is exactly the scattering the single-source rule
  // exists to prevent. Shape/padding stay at the call site; only colour lives here.
  button: {
    primary:   "bg-indigo-600 hover:bg-indigo-500 text-white font-medium disabled:opacity-40 disabled:cursor-not-allowed",
    // Same shape, dimmed — for aria-disabled buttons that must stay hoverable
    // so their title hint can explain what is missing. No cursor-not-allowed:
    // the point is to invite the tooltip, not to slam a door.
    primaryIdle: "bg-indigo-600/40 text-white/70 font-medium cursor-default",
    secondary: "text-canvas-muted hover:text-canvas-text hover:bg-zinc-800",
    danger:    "bg-red-600 hover:bg-red-500 text-white font-medium",
    ghost:     "text-canvas-muted hover:text-canvas-text",
  },
} as const;

// ── Node-page categories (design mock `CAT`) ─────────────────────────────────
// Shared by NodePageDrawer + FocusModeEditor for category icon tints and pills.
// Full literal Tailwind class strings so the scanner emits them. Hues match the
// mock: question=violet, info=sky, deep-dive=indigo, critical=amber.
export const PAGE_CATEGORY: Record<
  string,
  { color: string; tint: string; pill: string }
> = {
  question:    { color: "text-violet-400", tint: "bg-violet-400/10", pill: "text-violet-400 bg-violet-400/10" },
  info:        { color: "text-sky-400",    tint: "bg-sky-400/10",    pill: "text-sky-400 bg-sky-400/10" },
  "deep-dive": { color: "text-indigo-400", tint: "bg-indigo-400/10", pill: "text-indigo-400 bg-indigo-400/10" },
  critical:    { color: "text-amber-500",  tint: "bg-amber-500/10",  pill: "text-amber-500 bg-amber-500/10" },
};
export function pageCategoryStyle(cat: string) {
  return PAGE_CATEGORY[cat] ?? PAGE_CATEGORY.info;
}

// ── Node color override palette ──────────────────────────────────────────────
// Swatch grid shown in PropertiesPanel to override a node's default type color.
// `name` is persisted on CanvasNode.color; iconClass overrides NodeCard's icon color.
export const NODE_COLOR_PALETTE = [
  { name: "slate",   iconClass: "text-slate-500",   swatchClass: "bg-slate-500" },
  { name: "indigo",  iconClass: "text-indigo-500",  swatchClass: "bg-indigo-500" },
  { name: "violet",  iconClass: "text-violet-500",  swatchClass: "bg-violet-500" },
  { name: "purple",  iconClass: "text-purple-500",  swatchClass: "bg-purple-500" },
  { name: "cyan",    iconClass: "text-cyan-500",    swatchClass: "bg-cyan-500" },
  { name: "emerald", iconClass: "text-emerald-500", swatchClass: "bg-emerald-500" },
  { name: "amber",   iconClass: "text-amber-500",   swatchClass: "bg-amber-500" },
  { name: "sky",     iconClass: "text-sky-500",     swatchClass: "bg-sky-500" },
  { name: "blue",    iconClass: "text-blue-500",    swatchClass: "bg-blue-500" },
  { name: "rose",    iconClass: "text-rose-500",    swatchClass: "bg-rose-500" },
  { name: "red",     iconClass: "text-red-500",     swatchClass: "bg-red-500" },
  { name: "green",   iconClass: "text-green-500",   swatchClass: "bg-green-500" },
  { name: "teal",    iconClass: "text-teal-500",    swatchClass: "bg-teal-500" },
  { name: "orange",  iconClass: "text-orange-500",  swatchClass: "bg-orange-500" },
  { name: "fuchsia", iconClass: "text-fuchsia-500", swatchClass: "bg-fuchsia-500" },
  { name: "yellow",  iconClass: "text-yellow-500",  swatchClass: "bg-yellow-500" },
] as const;

// Palette name → Tailwind text-color class (used for drawing-stroke ink via
// `stroke: currentColor`). Falls back to indigo for unknown names.
const NODE_COLOR_ICON_CLASS: Record<string, string> = Object.fromEntries(
  NODE_COLOR_PALETTE.map((c) => [c.name, c.iconClass]),
);
export function colorIconClass(name: string): string {
  return NODE_COLOR_ICON_CLASS[name] ?? NODE_COLOR_ICON_CLASS.indigo;
}
